// @ts-nocheck
/**
 * Collaborative Editor Component
 * Real-time collaborative text editing with operational transforms
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { TextArea, Button, ButtonSet, Toggle, Tooltip, InlineNotification } from '@carbon/react';
import {
  Save,
  Undo,
  Redo,
  TextBold,
  TextItalic,
  TextUnderline,
  List,
  ListNumbered,
  Warning,
} from '@carbon/react/icons';

import { useCollaboration } from '../../hooks/useCollaboration';
import type { EditOperation } from '../../services/collaborationWebSocketService';

export interface CollaborativeEditorProps {
  collaboration: ReturnType<typeof useCollaboration>;
  contentData?: any;
  onContentChange?: (content: string) => void;
  onSave?: (content: string) => void;
  readOnly?: boolean;
  className?: string;
}

interface OperationTransform {
  type: 'insert' | 'delete' | 'retain';
  position: number;
  content?: string;
  length?: number;
  attributes?: Record<string, any>;
}

export const CollaborativeEditor: React.FC<CollaborativeEditorProps> = ({
  collaboration,
  contentData,
  onContentChange,
  onSave,
  readOnly = false,
  className = '',
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [content, setContent] = useState(contentData?.content || '');
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [operationQueue, setOperationQueue] = useState<EditOperation[]>([]);
  const [conflictResolution, setConflictResolution] = useState<any>(null);
  const [selectionRange, setSelectionRange] = useState<{ start: number; end: number } | null>(null);

  const { sendEditOperation, hasCapability, isConnected } = collaboration;

  // Generate unique operation ID
  const generateOperationId = useCallback(() => {
    return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Apply operational transform
  const applyOperation = useCallback((operation: EditOperation) => {
    setContent(prevContent => {
      let newContent = prevContent;

      switch (operation.type) {
        case 'insert':
          if (operation.content && operation.position <= newContent.length) {
            newContent =
              newContent.slice(0, operation.position) + operation.content + newContent.slice(operation.position);
          }
          break;

        case 'delete':
          if (operation.length && operation.position <= newContent.length) {
            const endPos = Math.min(operation.position + operation.length, newContent.length);
            newContent = newContent.slice(0, operation.position) + newContent.slice(endPos);
          }
          break;

        case 'retain':
          // Retain operation doesn't change content, used for transforms
          break;

        case 'format':
          // Format operation would apply styling attributes
          if (operation.attributes && operation.length) {
            // This would typically be handled by a rich text editor
            // TODO: Implement format operation handling
          }
          break;
      }

      return newContent;
    });
  }, []);

  // Handle text selection changes
  const handleSelectionChange = useCallback(() => {
    if (!textAreaRef.current || !hasCapability('selection_sharing')) return;

    const textarea = textAreaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    if (start !== end) {
      const selectedText = content.slice(start, end);

      collaboration.updateSelection({
        startOffset: start,
        endOffset: end,
        elementId: 'collaborative-editor',
        selectedText,
      });

      setSelectionRange({ start, end });
    } else {
      setSelectionRange(null);
    }
  }, [content, collaboration, hasCapability]);

  // Handle text changes
  const handleContentChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!hasCapability('collaborative_editing') || readOnly) return;

      const newContent = event.target.value;
      const oldContent = content;

      // Calculate the difference
      let operation: EditOperation | null = null;

      if (newContent.length > oldContent.length) {
        // Insert operation
        const insertPosition = findInsertPosition(oldContent, newContent);
        const insertedText = newContent.slice(insertPosition, insertPosition + (newContent.length - oldContent.length));

        operation = {
          operationId: generateOperationId(),
          type: 'insert',
          position: insertPosition,
          content: insertedText,
        };
      } else if (newContent.length < oldContent.length) {
        // Delete operation
        const deletePosition = findDeletePosition(oldContent, newContent);
        const deleteLength = oldContent.length - newContent.length;

        operation = {
          operationId: generateOperationId(),
          type: 'delete',
          position: deletePosition,
          length: deleteLength,
        };
      }

      setContent(newContent);
      setHasUnsavedChanges(true);
      onContentChange?.(newContent);

      // Send operation to other participants
      if (operation && isConnected) {
        sendEditOperation(operation);
        setOperationQueue(prev => [...prev, operation]);
      }
    },
    [content, hasCapability, readOnly, isConnected, sendEditOperation, generateOperationId, onContentChange]
  );

  // Helper function to find insert position
  const findInsertPosition = (oldText: string, newText: string): number => {
    for (let i = 0; i < Math.min(oldText.length, newText.length); i++) {
      if (oldText[i] !== newText[i]) {
        return i;
      }
    }
    return oldText.length;
  };

  // Helper function to find delete position
  const findDeletePosition = (oldText: string, newText: string): number => {
    for (let i = 0; i < Math.min(oldText.length, newText.length); i++) {
      if (oldText[i] !== newText[i]) {
        return i;
      }
    }
    return newText.length;
  };

  // Handle save
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(content);
      setHasUnsavedChanges(false);
    }
  }, [content, onSave]);

  // Handle undo/redo (simplified implementation)
  const handleUndo = useCallback(() => {
    // This would typically integrate with a proper undo/redo system
    // TODO: Implement undo functionality
  }, []);

  const handleRedo = useCallback(() => {
    // This would typically integrate with a proper undo/redo system
    // TODO: Implement redo functionality
  }, []);

  // Listen for document edits from other participants
  useEffect(() => {
    const handleDocumentEdited = (operation: EditOperation) => {
      applyOperation(operation);
    };

    const handleEditConflict = (conflictData: any) => {
      setConflictResolution(conflictData);
    };

    collaboration.collaboration.on('document_edited', handleDocumentEdited);
    collaboration.collaboration.on('edit_conflict', handleEditConflict);

    return () => {
      collaboration.collaboration.removeListener('document_edited', handleDocumentEdited);
      collaboration.collaboration.removeListener('edit_conflict', handleEditConflict);
    };
  }, [collaboration, applyOperation]);

  // Set up selection tracking
  useEffect(() => {
    const textarea = textAreaRef.current;
    if (!textarea) return;

    textarea.addEventListener('select', handleSelectionChange);
    textarea.addEventListener('click', handleSelectionChange);
    textarea.addEventListener('keyup', handleSelectionChange);

    return () => {
      textarea.removeEventListener('select', handleSelectionChange);
      textarea.removeEventListener('click', handleSelectionChange);
      textarea.removeEventListener('keyup', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  // Auto-save functionality
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(() => {
      if (onSave) {
        handleSave();
      }
    }, 5000); // Auto-save after 5 seconds of inactivity

    return () => clearTimeout(autoSaveTimer);
  }, [content, hasUnsavedChanges, handleSave, onSave]);

  const canEdit = hasCapability('collaborative_editing') && !readOnly;

  return (
    <div className={`collaborative-editor ${className}`}>
      {/* Toolbar */}
      {canEdit && (
        <div className="collaborative-editor__toolbar">
          <ButtonSet>
            <Tooltip content="Save document">
              <Button kind="primary" size="sm" renderIcon={Save} onClick={handleSave} disabled={!hasUnsavedChanges}>
                Save
              </Button>
            </Tooltip>

            <Tooltip content="Undo">
              <Button kind="ghost" size="sm" renderIcon={Undo} onClick={handleUndo} disabled />
            </Tooltip>

            <Tooltip content="Redo">
              <Button kind="ghost" size="sm" renderIcon={Redo} onClick={handleRedo} disabled />
            </Tooltip>
          </ButtonSet>

          <div className="collaborative-editor__format-controls">
            <Toggle id="bold-toggle" labelText="Bold" size="sm" disabled />
            <Toggle id="italic-toggle" labelText="Italic" size="sm" disabled />
            <Toggle id="underline-toggle" labelText="Underline" size="sm" disabled />
          </div>

          {hasUnsavedChanges && (
            <div className="collaborative-editor__status">
              <InlineNotification kind="info" title="Unsaved changes" hideCloseButton lowContrast />
            </div>
          )}
        </div>
      )}

      {/* Conflict Resolution */}
      {conflictResolution && (
        <div className="collaborative-editor__conflict-resolution" role="alert" aria-live="assertive">
          <InlineNotification
            kind="warning"
            title="Edit conflict detected"
            subtitle="Your changes conflict with another participant's edits."
            aria-labelledby="conflict-title"
            aria-describedby="conflict-desc"
            actions={
              <Button
                kind="tertiary"
                size="sm"
                onClick={() => setConflictResolution(null)}
                aria-label="Resolve edit conflict"
              >
                Resolve
              </Button>
            }
            onCloseButtonClick={() => setConflictResolution(null)}
          />
          <span id="conflict-title" className="sr-only">
            Edit conflict detected
          </span>
          <span id="conflict-desc" className="sr-only">
            Your changes conflict with another participant's edits. Choose to resolve or dismiss.
          </span>
        </div>
      )}

      {/* Connection Status */}
      {!isConnected && (
        <div className="collaborative-editor__offline-notice" role="status" aria-live="polite">
          <InlineNotification
            kind="warning"
            title="Working offline"
            subtitle="Changes will sync when connection is restored."
            hideCloseButton
            lowContrast
            aria-label="Working offline. Changes will sync when connection is restored."
          />
        </div>
      )}

      {/* Editor */}
      <div className="collaborative-editor__content">
        <TextArea
          ref={textAreaRef}
          id="collaborative-editor"
          labelText={canEdit ? 'Collaborative document' : 'Document (read-only)'}
          helperText={
            canEdit
              ? 'Type to edit. Changes are synced in real-time with other participants.'
              : 'You have read-only access to this document.'
          }
          value={content}
          onChange={handleContentChange}
          onFocus={() => setIsEditing(true)}
          onBlur={() => setIsEditing(false)}
          readOnly={!canEdit}
          rows={20}
          className="collaborative-editor__textarea"
          data-element-id="collaborative-editor"
        />
      </div>

      {/* Editor Info */}
      <div className="collaborative-editor__info">
        <div className="collaborative-editor__stats">
          <span>Characters: {content.length}</span>
          <span>Words: {content.trim() ? content.trim().split(/\s+/).length : 0}</span>
          {operationQueue.length > 0 && <span>Pending operations: {operationQueue.length}</span>}
        </div>

        {selectionRange && (
          <div className="collaborative-editor__selection-info">
            Selection: {selectionRange.start}-{selectionRange.end}({selectionRange.end - selectionRange.start}{' '}
            characters)
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaborativeEditor;
